import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from './api'

/** Statuses that mean a read is still running. */
const RUNNING = new Set(['Pending', 'Ingesting'])

/**
 * A live line along the bottom while a website is being read.
 *
 * Reading a shop takes a minute or two, and a minute with nothing moving on screen is a minute
 * someone spends wondering whether it broke. The bar says it is working, then says what came of
 * it, and then tells the conversation to carry on by itself.
 */
export default function ReadingBar({ onFinished }) {
  const { t } = useTranslation()
  const [sites, setSites] = useState(null)
  const [done, setDone] = useState(null)
  const wasReading = useRef(false)

  useEffect(() => {
    let cancelled = false

    async function poll() {
      try {
        const current = await api.sites()
        if (cancelled) return

        setSites(current)

        const reading = current.some((site) => RUNNING.has(site.status))

        // The moment it stops: say what was found, and let the conversation move on without the
        // owner having to prod it.
        if (wasReading.current && !reading) {
          const products = current.reduce((total, site) => total + (site.productCount ?? 0), 0)

          setDone({ products, failed: current.every((site) => site.status === 'Failed') })
          onFinished?.(products)
        }

        wasReading.current = reading
      } catch {
        // A failed poll is not worth a message of its own; the next one is six seconds away.
      }
    }

    poll()

    // Six seconds, and only while the tab is in front of someone: a poll nobody is watching is a
    // request spent on nothing, and it counts against the same allowance the conversation uses.
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') poll()
    }, 6000)

    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [onFinished])

  // The finished line stays for a moment and then gets out of the way.
  useEffect(() => {
    if (!done) return undefined

    const timer = setTimeout(() => setDone(null), 12000)
    return () => clearTimeout(timer)
  }, [done])

  const reading = sites?.some((site) => RUNNING.has(site.status))

  if (!reading && !done) return null

  if (reading) {
    const site = sites.find((entry) => RUNNING.has(entry.status))

    return (
      <div className="reading" role="status" aria-live="polite">
        <div className="reading__text">{t('reading.working', { url: site?.url ?? '' })}</div>
        <div className="reading__track">
          <div className="reading__bar" />
        </div>
      </div>
    )
  }

  return (
    <div className={`reading reading--done${done.failed ? ' reading--bad' : ''}`} role="status" aria-live="polite">
      <div className="reading__text">
        {done.failed ? t('reading.failed') : t('reading.done', { count: done.products })}
      </div>
    </div>
  )
}
